import { enqueue, drainQueue } from './idb-queue'
import { secureFetch } from './auth'

export type OrderStatusValue =
  | 'PENDING'
  | 'CONFIRMED'
  | 'IN_KITCHEN'
  | 'AWAITING_RETAIL_PACK'
  | 'AWAITING_FINAL_CHECK'
  | 'READY_FOR_DISPATCH'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'CANCELLED'
  | 'REFUNDED'

// Mirrors master-layer/src/orders/order-status.enum.ts ALLOWED_TRANSITIONS, minus the
// CANCELLED/REFUNDED arms — those are the separate guarded-destructive Cancel action here,
// not part of the one-tap forward-advance. Keep in lockstep with the backend enum if it changes.
// R78: the kitchen path now stops at AWAITING_FINAL_CHECK — no source's targets include
// READY_FOR_DISPATCH; only final-check (cashier app) writes it.
export const FORWARD_TRANSITIONS: Record<OrderStatusValue, OrderStatusValue[]> = {
  PENDING: ['CONFIRMED'],
  CONFIRMED: ['IN_KITCHEN', 'AWAITING_FINAL_CHECK'],
  IN_KITCHEN: ['AWAITING_FINAL_CHECK', 'AWAITING_RETAIL_PACK'],
  AWAITING_RETAIL_PACK: ['AWAITING_FINAL_CHECK'],
  AWAITING_FINAL_CHECK: [],
  READY_FOR_DISPATCH: ['OUT_FOR_DELIVERY'],
  OUT_FOR_DELIVERY: ['DELIVERED'],
  DELIVERED: [],
  CANCELLED: [],
  REFUNDED: [],
}

// bench-HOP-ORDER-BOARD.CB-A route-back (Rook #9167 gavel, MUST-FIX money-path leak): the
// board/detail UI may advance ONLY prep-lane source states. READY_FOR_DISPATCH (handoff,
// read-only), OUT_FOR_DELIVERY + DELIVERED (delivery leg = Dispatch bench's writes), and
// terminals get ZERO advance buttons. Positive allowlist, not a denylist — the leak this
// replaces was born from a denylist that only blocked the target it named; a future new
// status can't leak into the dispatch/financial leg until someone deliberately adds it here.
export const PREP_ADVANCE_SOURCES = new Set<OrderStatusValue>([
  'PENDING', 'CONFIRMED', 'IN_KITCHEN', 'AWAITING_RETAIL_PACK',
])
export function getForwardAdvanceTargets(status: OrderStatusValue): OrderStatusValue[] {
  return PREP_ADVANCE_SOURCES.has(status) ? (FORWARD_TRANSITIONS[status] ?? []) : []
}

// Staff may cancel from any state that isn't already terminal — mirrors the backend's
// cancellableFrom predicate (canTransition(status, 'CANCELLED')).
export const CANCELLABLE_FROM = new Set<OrderStatusValue>([
  'PENDING', 'CONFIRMED', 'IN_KITCHEN', 'AWAITING_RETAIL_PACK', 'AWAITING_FINAL_CHECK', 'READY_FOR_DISPATCH', 'OUT_FOR_DELIVERY',
])

export interface OrderCustomer {
  masterCustomerId: string
  name: string | null
  email: string | null
  phoneNormalized: string | null
  languagePreference: string | null
  isTest: boolean
  sourceRecordCount: number
  wcOrderCount: number | null
  mamOrderCount: number | null
  mdOrderCount: number | null
  wcTotalSpentRaw: string | null
  mamTotalSpentRaw: string | null
  mdTotalSpentRaw: string | null
  totalSpentCurrency: string
}

export interface OrderEntity {
  id: string
  slug: string
  name: string
}

export interface ProductSnapshot {
  nameEn?: string
  nameAr?: string
  allergens?: string[]
  // R289: a Toters line with no mapping to our menu
  unmapped?: boolean
  // ux2 (d): already in the snapshot the server writes (repointImageUrls); typed here only.
  imageUrls?: string[]
  [key: string]: unknown
}

export interface VariantSnapshot {
  nameEn?: string
  nameAr?: string
  [key: string]: unknown
}

export interface OrderItemRow {
  id: string
  productSnapshot: ProductSnapshot
  variantSnapshot: VariantSnapshot | null
  quantity: number
  unitPrice: string
  lineTotal: string
  customizationsJson: unknown
  isPromoReward: boolean
  promoId: string | null
}

export interface OrderStatusHistoryEntry {
  id: string
  fromStatus: OrderStatusValue | null
  toStatus: OrderStatusValue
  changedAt: string
  notes: string | null
  changedBy: { id: string; email: string; name: string | null } | null
}

export interface OrderDeliveryZone {
  nameEn: string
  nameAr: string
}

export interface OrderDeliveryLocation {
  cityNameEn: string
  cityNameAr: string
}

export interface OrderDetail {
  id: string
  // ux2 (e): already in GET /admin/orders/:id; optional so an old row or fixture without it
  // still prints, through formatOrderId's id-tail fallback.
  shortCode?: string | null
  status: OrderStatusValue
  totalAmount: string
  currency: string
  lbpAmount: string | null
  usdAmount: string | null
  sourceSystem: string
  sourceOrderId: string | null
  channel: string
  orderType: 'SALE' | 'COMP' | 'LEGACY'
  placedAt: string
  notes: string | null
  streetName: string | null
  buildingFloor: string | null
  deliveryDate: string | null
  deliverySlot: string | null
  deliveryNotes: string | null
  giftMessage: string | null
  originalDeliveryFee: string | null
  appliedDeliveryFee: string | null
  fulfillmentMethod: 'delivery' | 'pickup' | null
  whatsappOptIn: boolean
  trackingToken: string
  customer: OrderCustomer
  entity: OrderEntity
  items: OrderItemRow[]
  statusHistory: OrderStatusHistoryEntry[]
  deliveryZone: OrderDeliveryZone | null
  deliveryLocation: OrderDeliveryLocation | null
  // R562: server-computed (master-layer orders.service.ts getOrder). Absent reads as false,
  // so an old payload or fixture never shows the fee action.
  feeCorrectable?: boolean
}

// bench-HOP-SAYS-WHY.CB-A: master-layer's global ApiExceptionFilter wraps every error as
// `{ error: { code, message } }` (master-layer/src/common/api-exception.filter.ts) — a few
// older sites here still only knew the legacy bare `{ message }` shape, so both are read.
// Tolerates a non-JSON/absent body (falls back to `HTTP <status>`) and joins an array
// `message` with '; ', exactly as OrderCreateError did before this reader existed.
export interface FailureBody {
  error?: { code?: string; message?: string | string[]; details?: Record<string, unknown> }
  message?: string | string[]
}

// A Response body reads once: this is the one read, `undefined` when it is not JSON.
export async function readFailureBody(res: Response): Promise<FailureBody | undefined> {
  try {
    return ((await res.json()) as FailureBody | null) ?? undefined
  } catch {
    return undefined
  }
}

export function messageFromBody(body: FailureBody | undefined, status: number): string {
  const raw = body?.error?.message ?? body?.message
  const message = Array.isArray(raw) ? raw.join('; ') : raw
  return message || `HTTP ${status}`
}

export async function failureMessage(res: Response): Promise<string> {
  return messageFromBody(await readFailureBody(res), res.status)
}

export class HttpError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

// The gateway's `/api` proxy translates the httpOnly `ops_session` cookie into an
// `Authorization: Bearer` header for ML — the browser sends the cookie automatically
// (same-origin), so no client-side token attach is needed or possible anymore.
export async function fetchOrder(id: string): Promise<OrderDetail> {
  const res = await secureFetch(`/api/admin/orders/${id}`)
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<OrderDetail>
}

// ─── Board (bench-HOP-ORDER-BOARD.CB-A) ───────────────────────────────────────
// v1 is read + triage + advance-through-PREP-only + READ-ONLY delivery/cash state
// (D7b) — the dispatch/driver/cash fields below are surfaced for display only; the
// delivery-leg WRITES (mark-OFD, build-run/assign, deliver+collect, run cash-up) are
// the separate Dispatch bench.

export interface BoardDispatchInfo {
  stopStatus: 'PENDING' | 'PICKED_UP' | 'DELIVERED' | 'EXCEPTION' | 'CANCELLED'
  cashCollected: boolean
  localCashCollectedAmount: string
  exceptionReason: string | null
  driverId: string | null
  runStatus: 'PENDING' | 'ASSIGNED' | 'IN_PROGRESS' | 'COMPLETED' | null
}

export interface BoardOrderRow {
  id: string
  // ux2 (e): already in GET /admin/orders (the list); same fallback as OrderDetail.shortCode.
  shortCode?: string | null
  status: OrderStatusValue
  totalAmount: string
  currency: string
  placedAt: string
  deliveryDate: string | null
  deliverySlot: string | null
  fulfillmentMethod: 'delivery' | 'pickup' | null
  customer: { masterCustomerId: string; name: string | null; phoneNormalized: string | null }
  entity: OrderEntity
  deliveryZone: OrderDeliveryZone | null
  deliveryLocation: OrderDeliveryLocation | null
  _count: { items: number }
  // D7(a) READ-join — null when the order has no DispatchStop yet (not in a run).
  dispatch: BoardDispatchInfo | null
}

export interface BoardListResponse {
  items: BoardOrderRow[]
  total: number
  page: number
  pageSize: number
}

export interface BoardListParams {
  status?: OrderStatusValue
  // D5: 'promised' orders by deliveryDate — the board's active-in-prep sort.
  sort?: 'promised'
  page?: number
  pageSize?: number
}

export async function fetchBoardOrders(params: BoardListParams = {}): Promise<BoardListResponse> {
  const qs = new URLSearchParams()
  if (params.status) qs.set('status', params.status)
  if (params.sort) qs.set('sort', params.sort)
  if (params.page) qs.set('page', String(params.page))
  if (params.pageSize) qs.set('pageSize', String(params.pageSize))
  const query = qs.toString()
  const res = await secureFetch(`/api/admin/orders${query ? `?${query}` : ''}`)
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<BoardListResponse>
}

// Queued (offline-safe) writes — same enqueue+drainQueue pattern as the cockpit
// ActivityFeed, so status/cancel actions taken on a spotty tablet connection survive.
// idb-queue's drainQueue attaches the CSRF header (secureFetch-equivalent) at send time.

// R264 (b): every order write below waits for the server's answer (`sendOrQueue`) and queues only
// when the fetch itself throws, so the caller can show the order and the reason of a refusal.
export async function advanceOrderStatus(
  id: string,
  toStatus: OrderStatusValue,
  opts?: { expectedStatus?: OrderStatusValue; overrideKitchen?: true },
): Promise<SendOutcome> {
  return sendOrQueue('PATCH', `/api/admin/orders/${id}/status`, { toStatus, ...opts })
}

// bench-HOP-CAN-FINAL-CHECK.CB-F: Bilal's final check, from HOP — same offline queue as
// advanceOrderStatus, a distinct endpoint (not a status PATCH) since this is the one writer
// of READY_FOR_DISPATCH (orders.service.ts finalCheck), not a forward-advance.
export async function finalCheckOrder(id: string, expectedTotalNow?: string): Promise<SendOutcome> {
  const path = `/api/orders/${id}/final-check`
  if (expectedTotalNow === undefined) return sendOrQueue('POST', path)
  return sendOrQueue('POST', path, { expectedTotalNow })
}

// R197 (e2): the server's guard (e1) takes the total in two-decimal form only. String
// arithmetic, not floats. An unreadable value throws; the caller then sends no field.
const TWO_DECIMALS = /^-?\d+\.\d{2}$/
export function twoDecimals(value: string): string {
  const m = /^(-?\d+)(?:\.(\d{1,2}))?$/.exec(typeof value === 'string' ? value.trim() : '')
  if (!m) throw new Error(`unreadable total: ${String(value)}`)
  return `${m[1]}.${(m[2] ?? '').padEnd(2, '0')}`
}

// R197 (b): the Board's one-click final check. The read is R197 (a)'s bare array; the fields
// below are the ones this button uses. Typed loosely-trusting on purpose: the card treats a
// non-boolean flag, a non-decimal total or an empty currency as unreadable and asks.
export interface FinalCheckFact {
  id: string
  moneyChanged: boolean
  totalNow: string
  totalAtPlacement: string
  currency: string
}

export async function fetchAwaitingFinalCheck(): Promise<FinalCheckFact[]> {
  const res = await secureFetch('/api/admin/orders/awaiting-final-check')
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<FinalCheckFact[]>
}

// Same three-way outcome as the cashier's SendOutcome (apps/cashier-pwa/src/lib/api.ts):
// `queued` only when no response arrived; any received non-2xx is `rejected`.
export type SendOutcome =
  | { outcome: 'sent' }
  | { outcome: 'queued'; uuid: string }
  | {
      outcome: 'rejected'
      status: number
      message: string
      code?: string
      details?: Record<string, unknown>
    }

// R197 (e2): the field is sent only when it is two decimals; otherwise it is omitted (today's
// unguarded call, logged server-side) so an unreadable-total order is not 400'd forever.
export async function sendFinalCheck(id: string, expectedTotalNow?: string): Promise<SendOutcome> {
  const path = `/api/orders/${id}/final-check`
  const guarded = typeof expectedTotalNow === 'string' && TWO_DECIMALS.test(expectedTotalNow)
  let res: Response
  try {
    res = guarded
      ? await secureFetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ expectedTotalNow }),
        })
      : await secureFetch(path, { method: 'POST' })
  } catch {
    // The uuid is what idb-queue's hop:reconcile / hop:action-failed events carry, so the
    // Board can tell which queued row an event is about.
    const uuid = guarded ? await enqueue('POST', path, { expectedTotalNow }) : await enqueue('POST', path)
    if (navigator.onLine) drainQueue().catch(() => {})
    return { outcome: 'queued', uuid }
  }
  if (res.ok) return { outcome: 'sent' }
  return { outcome: 'rejected', status: res.status, message: await failureMessage(res) }
}

// R264 (b): sendFinalCheck's sibling for the other order writes (sendFinalCheck stays as it is).
// A received non-2xx is `rejected` with the server's code and details; a thrown fetch queues.
export async function sendOrQueue(
  method: 'POST' | 'PATCH',
  path: string,
  body?: unknown,
): Promise<SendOutcome> {
  let res: Response
  try {
    res =
      body === undefined
        ? await secureFetch(path, { method })
        : await secureFetch(path, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
  } catch {
    const uuid = body === undefined ? await enqueue(method, path) : await enqueue(method, path, body)
    if (navigator.onLine) drainQueue().catch(() => {})
    return { outcome: 'queued', uuid }
  }
  if (res.ok) return { outcome: 'sent' }
  const parsed = await readFailureBody(res)
  return {
    outcome: 'rejected',
    status: res.status,
    message: messageFromBody(parsed, res.status),
    code: parsed?.error?.code,
    details: parsed?.error?.details,
  }
}

// A 409 STATUS_MOVED whose current status is the move's own target: the move already landed.
export function isAlreadyThere(outcome: SendOutcome, toStatus: OrderStatusValue): boolean {
  return (
    outcome.outcome === 'rejected' &&
    outcome.status === 409 &&
    outcome.code === 'STATUS_MOVED' &&
    outcome.details?.currentStatus === toStatus
  )
}

export async function cancelOrder(id: string): Promise<SendOutcome> {
  return sendOrQueue('POST', `/api/admin/orders/${id}/cancel`)
}

// D2 (bench-HOP-ORDER-BOARD.CB-A #9088 gavel A, upheld in part by bench-CANCEL-DOOR-CONVERGE.CB-A):
// Board's admin-only Reject action goes through a distinct ENDPOINT/actor path from
// cancelOrder() above — that split stands. Server-side, though, both converge: orders.service.ts's
// updateStatus() now delegates its CANCELLED arm to cancelOrder(), the only emitter of
// order.cancelled, so a board Reject drives the same event as a detail-page Cancel. Goes through
// the SAME shared PATCH .../status route as advanceOrderStatus, to CANCELLED, with a reason
// in `notes`. Server-side enforcement (the actual gate) is orders.service.ts's A3
// guard — this is admin-only regardless of who calls it.
export async function rejectOrder(
  id: string,
  notes: string,
  expectedStatus?: OrderStatusValue,
): Promise<SendOutcome> {
  return sendOrQueue('PATCH', `/api/admin/orders/${id}/status`, {
    toStatus: 'CANCELLED',
    notes,
    ...(expectedStatus ? { expectedStatus } : {}),
  })
}

// ─── Delivery cash (bench-HOP-MARK-DELIVERED-CASH.CB-A) ───────────────────────
// A direct request, NOT enqueue()'d like advanceOrderStatus/cancelOrder above: the caller
// (Mark delivered) must know SYNCHRONOUSLY whether the cash write landed before it is
// allowed to advance the status — record first, advance second, and a failed record here
// must never be followed by the PATCH. A 409 means the amount is already recorded (e.g. a
// retried tap) — not a failure; the caller proceeds to the status advance exactly as it
// would on a fresh 2xx, with no duplicate row.
export class DeliveryCashError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

export async function recordDeliveryCash(id: string, amountUsd: number): Promise<void> {
  const res = await secureFetch(`/api/orders/${id}/cash/base-reconciliation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ amountUsd }),
  })
  if (!res.ok && res.status !== 409) {
    throw new DeliveryCashError(await failureMessage(res), res.status)
  }
}

// ─── Create (bench-INTAKE-NEW-ORDER) ──────────────────────────────────────────
// Mirrors master-layer/src/orders/dto/create-order.dto.ts CreateOrderDto field-for-field.
// Server derives the entity from the customer, resolves the delivery fee and computes
// totals — none of that is duplicated client-side. Phone and WhatsApp orders are confirmed
// at create (R115); the other channels stay PENDING.

export interface CreateOrderItemInput {
  productId: string
  quantity: number
  variantId?: string
  customizationsJson?: Record<string, unknown>
}

export type StaffOrderChannel = 'phone' | 'whatsapp' | 'counter' | 'shop' | 'toters'

export interface CreateOrderInput {
  customerId: string
  items: CreateOrderItemInput[]
  notes?: string
  channel: StaffOrderChannel
  sourceOrderId?: string
  orderType?: 'SALE' | 'COMP' | 'LEGACY'
  currency?: string
  fxRateId?: string
  fulfillmentMethod?: 'delivery' | 'pickup'
  deliveryZoneId?: string
  deliveryLocationId?: string
  streetName?: string
  deliveryNotes?: string
  idempotencyKey: string
}

export interface CreatedOrder {
  id: string
  status: OrderStatusValue
}

export class OrderCreateError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

// A direct request, NOT enqueue()'d like advanceOrderStatus/cancelOrder above — the
// caller needs the created order's id back immediately to navigate to it, and the
// queue only ever returns a client-generated uuid, never server response data.
export async function createOrder(input: CreateOrderInput): Promise<CreatedOrder> {
  const res = await secureFetch('/api/admin/orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    throw new OrderCreateError(res.status, await failureMessage(res))
  }
  return res.json() as Promise<CreatedOrder>
}

// ─── Delivery fee change (bench-DELIVERY-FEE-CHANGE.CB-F) ─────────────────────
// R562 (Rook #68445): no client copy of the server's status rule. The order detail's
// feeCorrectable flag says whether the server would accept a change now, including a
// DELIVERED order whose invoice is still held.

export interface LinkedOrderStillCharges {
  code: string
  fee: string
}

export interface ChangeDeliveryFeeInput {
  fee: number
  reason: 'combined' | 'other'
  linkedOrderCode?: string
  note?: string
}

export interface ChangeDeliveryFeeResponse {
  ok: true
  order: { appliedDeliveryFee: string | null; totalAmount: string }
  gate: 'hold' | 'allow' | 'skipped'
  linkedOrderStillCharges?: LinkedOrderStillCharges
}

// A direct request, NOT enqueue()'d: the code lookup (for reason='combined') needs the
// server, so this write is online-only and disabled offline — unlike advanceOrderStatus/
// cancelOrder above, there is no useful optimistic/offline-queued version of it.
export async function changeDeliveryFee(id: string, body: ChangeDeliveryFeeInput): Promise<ChangeDeliveryFeeResponse> {
  const res = await secureFetch(`/api/admin/orders/${id}/delivery-fee`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new HttpError(await failureMessage(res), res.status)
  return res.json() as Promise<ChangeDeliveryFeeResponse>
}

// R459: Edit items. PATCH /admin/orders/:id/amend takes the WHOLE new item list (the server works
// out adds, removes and quantity changes), an optional reason, and expectedStatus (409
// STATUS_MOVED, no write, when the order moved). A direct request, NOT enqueue()'d: a queued item
// list could land after the cutoff or after another edit, so this write is online-only.
// A thrown fetch rethrows; any received non-2xx is `rejected` with the server's code.
export interface AmendItemInput {
  productId: string
  variantId?: string
  quantity: number
}

export type AmendOutcome =
  | { outcome: 'sent' }
  | { outcome: 'rejected'; status: number; message: string; code?: string }

export async function amendOrderItems(
  id: string,
  body: { items: AmendItemInput[]; reason?: string; expectedStatus: OrderStatusValue },
): Promise<AmendOutcome> {
  const res = await secureFetch(`/api/admin/orders/${id}/amend`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (res.ok) return { outcome: 'sent' }
  const parsed = await readFailureBody(res)
  return { outcome: 'rejected', status: res.status, message: messageFromBody(parsed, res.status), code: parsed?.error?.code }
}

// Canonical customer-facing order reference — mirrors web/lib/format-order-id.ts and
// master-layer/src/notifications/order-id.ts BYTE-IDENTICAL (#8998 destination-proven).
// Same string as the WhatsApp order_confirmation template and the storefront receipt —
// never a second ID for the same order.
// ux2 (e), Dee 2026-10-30: TKF- plus the order's stored shortCode in every app. The shortCode is
// minted as the same id tail (orders.service.ts), so the printed code does not move; an order
// without one (old rows) falls back to the last six characters of the id, never the raw id.
export function formatOrderId(orderId: string, shortCode?: string | null): string {
  const code = typeof shortCode === 'string' ? shortCode.trim() : ''
  return `TKF-${(code || orderId.slice(-6)).toUpperCase()}`
}
